/* ==========================================================================
   Avéré — <avere-hero>
   sections/hero.liquid

   The poster still is what the page is graded on, so no video byte is
   requested until the page has loaded and the browser has gone idle. Until
   then the still holds the frame with its slow drift.

   ONE CLIP  → loads, fades over the still, loops.
   SEVERAL   → clip 1 loads alone and plays; clip 2 starts downloading only
               once clip 1 is on screen, and cross-fades in near its end.

   ========================================================================== */
(function () {
  'use strict';

  if (customElements.get('avere-hero')) return;

  var XFADE = 0.9;   // seconds; match --xfade in blocks.css

  class AvereHero extends HTMLElement {
    connectedCallback() {
      this.reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.onScroll = this.onScroll.bind(this);
      this.onVisibility = this.onVisibility.bind(this);
      window.addEventListener('scroll', this.onScroll, { passive: true });
      document.addEventListener('visibilitychange', this.onVisibility);
      this.onScroll();
      this.setupVideo();
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', this.onScroll);
      document.removeEventListener('visibilitychange', this.onVisibility);
      if (this.io) this.io.disconnect();
      this.pauseAll();
    }

    // .is-moved once the page has scrolled (a styling hook)
    onScroll() {
      this.classList.toggle('is-moved', window.scrollY > 40);
    }

    /* ---- Film ----------------------------------------------------------- */
    setupVideo() {
      this.layers = this.querySelectorAll('.hero__video');
      this.clips = Array.prototype.map.call(this.querySelectorAll('template.hero__clip'), function (t) {
        return Array.prototype.map.call(t.content.querySelectorAll('source'), function (s) {
          return { src: s.getAttribute('data-src'), type: s.getAttribute('type'), width: +s.getAttribute('data-width') || 0 };
        });
      }).filter(function (list) { return list.length; });

      if (!this.clips.length || this.layers.length < 1) return;
      if (this.reduce) return;
      if (window.innerWidth < (+this.dataset.minWidth || 0)) return;

      // Respect Data Saver and genuinely slow connections (Chromium only).
      var conn = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
      if (conn && (conn.saveData || /^(slow-)?2g$/.test(conn.effectiveType || ''))) return;

      // The rendition: smallest that covers the screen, capped at 1920.
      this.want = Math.min(1920, Math.round(window.innerWidth * Math.min(window.devicePixelRatio || 1, 2)));

      this.a = this.layers[0];
      this.b = this.layers[1] || null;
      this.single = this.clips.length === 1 || !this.b;
      this.current = this.a;
      this.standby = this.b;
      this.index = 0;
      this.armed = false;
      this.revealed = false;
      this.onscreen = true;

      var self = this;
      if ('IntersectionObserver' in window) {
        // don't decode a video that's scrolled past — it's pure battery burn
        this.io = new IntersectionObserver(function (entries) {
          self.onscreen = entries[0].isIntersecting;
          if (!self.onscreen) self.pauseAll();
          else if (self.revealed && !document.hidden) self.play(self.current);
        }, { threshold: 0.01 });
        this.io.observe(this);
      }

      var begin = function () { self.begin(); };
      var afterIdle = function () {
        if (window.requestIdleCallback) requestIdleCallback(begin, { timeout: 1500 });
        else setTimeout(begin, 300);
      };
      if (document.readyState === 'complete') afterIdle();
      else window.addEventListener('load', afterIdle, { once: true });
    }

    pick(list) {
      // Prefer mp4 (plays everywhere); among those, the smallest width that
      // still covers the screen, else the largest there is.
      var mp4 = list.filter(function (s) { return /mp4/.test(s.type); });
      var pool = (mp4.length ? mp4 : list).slice().sort(function (x, y) { return x.width - y.width; });
      var want = this.want;
      for (var i = 0; i < pool.length; i++) if (pool[i].width >= want) return pool[i];
      return pool[pool.length - 1];
    }

    fill(video, list) {
      var s = this.pick(list);
      video.src = s.src;
    }

    reveal() {
      if (this.revealed) return;
      this.revealed = true;
      this.classList.add('video-ready');
    }

    play(video) {
      var self = this;
      var p = video.play();
      if (p && typeof p.catch === 'function') {
        p.catch(function () {
          // Autoplay refused (iOS Low Power Mode does it, even muted). Take
          // the first gesture and try again.
          document.addEventListener('pointerdown', function () {
            video.play().then(function () { self.reveal(); }).catch(function () {});
          }, { once: true });
        });
      }
    }

    begin() {
      if (!this.isConnected) return;
      var self = this;
      this.current.classList.add('is-active');
      this.current.loop = this.single;
      this.fill(this.current, this.clips[0]);
      this.current.addEventListener('canplay', function () { self.reveal(); }, { once: true });
      this.current.addEventListener('error', function () {
        // unplayable: the still is already correct underneath
        if (!self.revealed) self.classList.remove('video-ready');
      }, { once: true });
      this.current.load();
      this.play(this.current);

      if (!this.single) {
        this.current.addEventListener('playing', function () { self.prepareNext(); }, { once: true });
        this.watch();
      }
    }

    prepareNext() {
      var next = (this.index + 1) % this.clips.length;
      this.fill(this.standby, this.clips[next]);
      this.standby.preload = 'auto';
      this.standby.load();
    }

    watch() {
      var self = this;
      this.layers.forEach(function (layer) {
        layer.addEventListener('timeupdate', function () {
          if (layer !== self.current || self.armed) return;
          var d = layer.duration;
          if (!d || isNaN(d) || d - layer.currentTime > XFADE) return;
          self.armed = true;
          self.play(self.standby);
          // wait for a real frame before fading, or the cross-fade shows black
          if (self.standby.readyState >= 2) self.swap();
          else self.standby.addEventListener('canplay', function () { self.swap(); }, { once: true });
        });
      });
    }

    swap() {
      var outgoing = this.current;
      outgoing.classList.remove('is-active');
      this.standby.classList.add('is-active');
      this.current = this.standby;
      this.standby = outgoing;
      this.index = (this.index + 1) % this.clips.length;
      this.armed = false;
      var self = this;
      // let the fade finish before resetting the layer we just left
      setTimeout(function () {
        outgoing.pause();
        try { outgoing.currentTime = 0; } catch (e) { /* not seekable yet */ }
        self.prepareNext();
      }, XFADE * 1000 + 120);
    }

    pauseAll() {
      if (this.a) this.a.pause();
      if (this.b) this.b.pause();
    }

    onVisibility() {
      if (!this.current) return;
      if (document.hidden) this.pauseAll();
      else if (this.revealed && this.onscreen) this.play(this.current);
    }
  }

  customElements.define('avere-hero', AvereHero);
})();
